import type { PreVisitPhoto, PreVisitQuestion } from '@/api/preVisit/preVisitApi.types';
import { PRE_VISIT_QUESTION_TYPE } from '@/constants/preVisitQuestionTypes';
import { DateRenderer } from './DateRenderer';
import { LongTextRenderer } from './LongTextRenderer';
import { MultipleChoiceRenderer } from './MultipleChoiceRenderer';
import { NumberRenderer } from './NumberRenderer';
import { PhotosRenderer } from './PhotosRenderer';
import { SectionHeadingRenderer } from './SectionHeadingRenderer';
import { ShortTextRenderer } from './ShortTextRenderer';
import { SingleChoiceRenderer } from './SingleChoiceRenderer';
import { YesNoRenderer } from './YesNoRenderer';

interface PreVisitQuestionFieldProps {
  question: PreVisitQuestion;
  value: string | string[] | undefined;
  photos: PreVisitPhoto[];
  isInvalid: boolean;
  isUploadingPhoto: boolean;
  isPhotoActionDisabled: boolean;
  onChange: (value: string | string[]) => void;
  onUploadPhoto: (file: File) => void;
  onRemovePhoto: (photo: PreVisitPhoto) => void;
  onPreviewPhoto: (photo: PreVisitPhoto) => void;
}

export function PreVisitQuestionField({
  question,
  value,
  photos,
  isInvalid,
  isUploadingPhoto,
  isPhotoActionDisabled,
  onChange,
  onUploadPhoto,
  onRemovePhoto,
  onPreviewPhoto,
}: PreVisitQuestionFieldProps) {
  if (question.questionType === PRE_VISIT_QUESTION_TYPE.sectionHeading) {
    return <SectionHeadingRenderer question={question} />;
  }
  if (question.questionType === PRE_VISIT_QUESTION_TYPE.longText) {
    return <LongTextRenderer question={question} value={value} isInvalid={isInvalid} onChange={onChange} />;
  }
  if (question.questionType === PRE_VISIT_QUESTION_TYPE.yesNo) {
    return <YesNoRenderer question={question} value={value} isInvalid={isInvalid} onChange={onChange} />;
  }
  if (question.questionType === PRE_VISIT_QUESTION_TYPE.singleChoice) {
    return (
      <SingleChoiceRenderer question={question} value={value} isInvalid={isInvalid} onChange={onChange} />
    );
  }
  if (question.questionType === PRE_VISIT_QUESTION_TYPE.multipleChoice) {
    return (
      <MultipleChoiceRenderer question={question} value={value} isInvalid={isInvalid} onChange={onChange} />
    );
  }
  if (question.questionType === PRE_VISIT_QUESTION_TYPE.number) {
    return <NumberRenderer question={question} value={value} isInvalid={isInvalid} onChange={onChange} />;
  }
  if (question.questionType === PRE_VISIT_QUESTION_TYPE.date) {
    return <DateRenderer question={question} value={value} isInvalid={isInvalid} onChange={onChange} />;
  }
  if (question.questionType === PRE_VISIT_QUESTION_TYPE.photos) {
    return (
      <PhotosRenderer
        question={question}
        photos={photos}
        isInvalid={isInvalid}
        isUploading={isUploadingPhoto}
        isDisabled={isPhotoActionDisabled}
        onUpload={onUploadPhoto}
        onRemove={onRemovePhoto}
        onPreview={onPreviewPhoto}
      />
    );
  }
  return <ShortTextRenderer question={question} value={value} isInvalid={isInvalid} onChange={onChange} />;
}
